import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import { parseForemanResponse } from '../gateway/parser';
import { buildMessageSend } from '../gateway/protocol';
import { fileToAttachment } from '../gateway/protocol';
import { isExtractableDocument, extractDocumentText } from '../extractors';

interface UseMessagesProps {
  send: (data: unknown) => void;
  onMessage: (handler: (data: unknown) => void) => () => void;
  activeSessionId: string | null;
  teachMode?: boolean;
}

export function useMessages({ send, onMessage, activeSessionId, teachMode = false }: UseMessagesProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamBufferRef = useRef<string>('');

  useEffect(() => {
    const unsub = onMessage((data: unknown) => {
      const msg = data as { type?: string; data?: Record<string, unknown> };

      if (msg.type === 'message.receive') {
        const payload = msg.data;
        if (!payload) return;

        const content = (payload.content as string) || '';
        const streaming = payload.streaming as boolean;
        const done = payload.done as boolean;
        const msgId = (payload.id as string) || crypto.randomUUID();

        if (streaming && !done) {
          setIsStreaming(true);
          streamBufferRef.current += content;

          setMessages((prev) => {
            const existing = prev.find((m) => m.id === msgId);
            const parsed = parseForemanResponse(streamBufferRef.current);

            if (existing) {
              return prev.map((m) =>
                m.id === msgId ? { ...m, content: streamBufferRef.current, parsed } : m
              );
            }
            return [
              ...prev,
              {
                id: msgId,
                type: 'foreman',
                content: streamBufferRef.current,
                parsed,
                sender: 'Foreman',
                timestamp: new Date().toISOString(),
                sessionId: (payload.sessionId as string) || activeSessionId || '',
              },
            ];
          });
        } else {
          // Message complete
          setIsStreaming(false);
          const finalContent = streaming ? streamBufferRef.current : content;
          const parsed = parseForemanResponse(finalContent);
          streamBufferRef.current = '';

          setMessages((prev) => {
            const existing = prev.find((m) => m.id === msgId);
            if (existing) {
              return prev.map((m) =>
                m.id === msgId ? { ...m, content: finalContent, parsed } : m
              );
            }
            return [
              ...prev,
              {
                id: msgId,
                type: 'foreman',
                content: finalContent,
                parsed,
                sender: 'Foreman',
                timestamp: new Date().toISOString(),
                sessionId: (payload.sessionId as string) || activeSessionId || '',
              },
            ];
          });
        }
      }
    });

    return unsub;
  }, [onMessage, activeSessionId]);

  const sendMessage = useCallback(
    async (content: string, files?: File[]) => {
      if (!content.trim() && (!files || files.length === 0)) return;

      // When teach mode is active, prefix the content with a training signal
      // so the gateway/agent (foreman-learning skill) treats it as training input
      let gatewayContent = teachMode ? `[TEACH] ${content}` : content;

      // Add user message to local state (show original content to user)
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        type: 'user',
        content,
        sender: 'You',
        timestamp: new Date().toISOString(),
        sessionId: activeSessionId || '',
      };
      setMessages((prev) => [...prev, userMsg]);

      // Separate files: images go as attachments, documents get text extracted
      const imageFiles: File[] = [];
      const docFiles: File[] = [];
      if (files) {
        for (const f of files) {
          if (isExtractableDocument(f)) {
            docFiles.push(f);
          } else {
            imageFiles.push(f);
          }
        }
      }

      // Extract text from documents client-side
      if (docFiles.length > 0) {
        const results = await Promise.all(docFiles.map(extractDocumentText));
        const blocks: string[] = [];
        for (const result of results) {
          if (result.ok) {
            const ext = result.fileName.split('.').pop()?.toUpperCase() || 'FILE';
            blocks.push(`[Extracted from ${result.fileName} (${ext})]:\n\n${result.text}`);
          } else {
            blocks.push(`[Failed to extract ${result.fileName}: ${result.error}. Please paste the text directly or send a photo/screenshot.]`);
          }
        }
        if (blocks.length > 0) {
          gatewayContent = gatewayContent
            ? gatewayContent + '\n\n' + blocks.join('\n\n')
            : blocks.join('\n\n');
        }
      }

      // Build attachments from image files only
      const attachments = imageFiles.length > 0
        ? await Promise.all(imageFiles.map(fileToAttachment))
        : undefined;

      // Send to gateway with teach-mode prefix when applicable
      send(buildMessageSend(gatewayContent, activeSessionId || undefined, attachments));
    },
    [send, activeSessionId, teachMode]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isStreaming,
    sendMessage,
    clearMessages,
  };
}
